import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getStoredRole, getTopNavLinks } from '../utils/navigation';

const C = { bg: '#0A0E1A', surface: '#111827', border: '#1F2937', accent: '#10B981', text: '#F9FAFB', muted: '#6B7280' };
const s = {
  page: { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans','Segoe UI',sans-serif" },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px', borderBottom: `1px solid ${C.border}`, background: C.surface },
  logo: { fontSize: 20, fontWeight: 700, color: C.accent },
  nav: { display: 'flex', gap: 24, flexWrap: 'wrap' },
  navLink: (active) => ({ color: active ? C.accent : C.muted, textDecoration: 'none', fontSize: 14, fontWeight: active ? 700 : 400 }),
  main: { padding: 32, maxWidth: 1240, margin: '0 auto' },
  heading: { fontSize: 26, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 14, color: C.muted, marginBottom: 24 },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '12px 16px', borderBottom: `1px solid ${C.border}` },
  td: { padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${C.border}22` },
  empty: { textAlign: 'center', padding: '48px 0', color: C.muted }
};

export default function Merchants() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const role = getStoredRole();
  const links = getTopNavLinks(role);
  const canReview = ['superadmin', 'admin', 'compliance_officer'].includes(role);

  const loadMerchants = async () => {
    const r = await api.get('/merchants');
    setItems(Array.isArray(r.data) ? r.data : []);
  };

  useEffect(() => {
    const load = async () => {
      try {
        await loadMerchants();
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleKyc = async (merchantId, decision) => {
    setUpdatingId(merchantId);
    setError('');
    setSuccess('');
    try {
      await api.post(`/merchants/${merchantId}/kyc`, { decision });
      await loadMerchants();
      setSuccess(`KYC actualizado para comercio #${merchantId}`);
    } catch (e) {
      setError(e?.response?.data?.error || 'No se pudo actualizar el KYC');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.logo}>⚡ FintechCoop</div>
        <nav style={s.nav}>{links.map(link => <Link key={link.to} to={link.to} style={s.navLink(link.to === '/merchants')}>{link.label}</Link>)}</nav>
      </div>
      <div style={s.main}>
        <div style={s.heading}>Comercios</div>
        <div style={s.sub}>Gestión de comercios, estado KYC y proveedor de pagos</div>
        {error && <div style={{ marginBottom: 12, color: '#EF4444', fontSize: 13 }}>{error}</div>}
        {success && <div style={{ marginBottom: 12, color: C.accent, fontSize: 13 }}>{success}</div>}
        <div style={s.card}>
          {loading ? <div style={s.empty}>Cargando comercios...</div> : items.length === 0 ? <div style={s.empty}>Sin datos disponibles</div> : (
            <table style={s.table}>
              <thead><tr>{['ID', 'Nombre', 'Email', 'Estado', 'KYC', 'Proveedor', ...(canReview ? ['Acciones'] : [])].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td style={s.td}>{m.id}</td>
                    <td style={s.td}>{m.business_name || '—'}</td>
                    <td style={s.td}>{m.email || '—'}</td>
                    <td style={s.td}>{m.status || '—'}</td>
                    <td style={s.td}>{m.kyc_status || '—'}</td>
                    <td style={s.td}>{m.payment_provider || '—'}</td>
                    {canReview && (
                      <td style={s.td}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            type="button"
                            disabled={updatingId === m.id || m.kyc_status === 'verified'}
                            onClick={() => handleKyc(m.id, 'approve')}
                            style={{ background: C.accent, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', opacity: updatingId === m.id || m.kyc_status === 'verified' ? 0.6 : 1 }}
                          >
                            Aprobar
                          </button>
                          <button
                            type="button"
                            disabled={updatingId === m.id || m.kyc_status === 'rejected'}
                            onClick={() => handleKyc(m.id, 'reject')}
                            style={{ background: '#374151', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', opacity: updatingId === m.id || m.kyc_status === 'rejected' ? 0.6 : 1 }}
                          >
                            Rechazar
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
